/**
 * Instanced Billboard-Renderer für Supernova-Trümmer.
 * Pool von 64 Partikeln, pre-allocated. Radiale Ausbreitung
 * vom Explosionszentrum. Additive Blending, HDR für Bloom.
 */

import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import { createQuad } from '../renderer/GeometryFactory';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { SunLifecycleEntry } from '../systems/SunLifecycleTypes';
import { SunState } from '../systems/SunLifecycleTypes';
import type { ClusterContent } from '../network/ContentLoader';
import { axialToWorld } from '../math/HexMath';

import vertSrc from '../shaders/supernova-debris.vert.glsl?raw';
import fragSrc from '../shaders/supernova-debris.frag.glsl?raw';

const MAX_PARTICLES = 64;
const FLOATS_PER_PARTICLE = 6; // worldPos(3), size(1), alpha(1), seed(1)
const PARTICLE_SPEED = 5.0;
const PARTICLE_LIFETIME = 2.0; // Sekunden
const PARTICLE_SIZE = 0.25;

interface ParticleSlot {
  active: boolean;
  originX: number; originZ: number;
  dirX: number; dirY: number; dirZ: number;
  speed: number;
  elapsed: number;
  seed: number;
}

export class SupernovaDebrisRenderer implements GameModule, Renderable {
  readonly name = 'SupernovaDebris';

  private _gl!: WebGL2RenderingContext;
  private _shader!: ShaderProgram;
  private _mesh!: InstancedMesh;
  private _instanceBuf = new Float32Array(MAX_PARTICLES * FLOATS_PER_PARTICLE);
  private _particles: ParticleSlot[] = [];
  private _activeCount = 0;
  private _spawned = new Set<string>();
  private _sunPositions = new Map<string, [number, number]>();
  private _hexRadius: number;

  constructor(hexRadius: number) {
    this._hexRadius = hexRadius;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this._particles.push({
        active: false, originX: 0, originZ: 0,
        dirX: 0, dirY: 0, dirZ: 0, speed: 0, elapsed: 0, seed: 0,
      });
    }
  }

  init(engine: Engine): void {
    this._gl = engine.gl;
    const gl = this._gl;

    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);
    const quad = createQuad();
    this._mesh = new InstancedMesh(
      gl, this._shader, quad.positions,
      [{ name: 'a_corner', size: 2, divisor: 0 }],
      [
        { name: 'a_worldPos', size: 3, divisor: 1 },
        { name: 'a_size', size: 1, divisor: 1 },
        { name: 'a_alpha', size: 1, divisor: 1 },
        { name: 'a_seed', size: 1, divisor: 1 },
      ],
    );

    engine.events.on<ClusterContent[]>('sun:stars-available', (contents) => {
      this._buildPositionMap(contents);
    });
    engine.events.on<ReadonlyMap<string, SunLifecycleEntry>>(
      'sun:lifecycle-update', (data) => { this._checkSpawn(data); },
    );
  }

  update(dt: number): void {
    this._updateParticles(dt);
    this._uploadInstances();
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._activeCount === 0) return;

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.depthMask(false);

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    const v = camera.view.data;
    this._shader.setVec3('u_cameraRight', v[0], v[4], v[8]);
    this._shader.setVec3('u_cameraUp', v[1], v[5], v[9]);
    this._mesh.draw();

    gl.depthMask(true);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  }

  dispose(): void {
    this._shader?.dispose();
    this._mesh?.dispose();
    this._spawned.clear();
  }

  /** Baut Positions-Map aus Content-Daten. */
  private _buildPositionMap(contents: ClusterContent[]): void {
    this._sunPositions.clear();
    for (let c = 0; c < contents.length; c++) {
      const stars = contents[c].stars;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        if (!s.entity_id || s.axial_q == null || s.axial_r == null) continue;
        const [wx, wz] = axialToWorld(s.axial_q, s.axial_r, this._hexRadius);
        this._sunPositions.set(s.entity_id, [wx, wz]);
      }
    }
  }

  /** Prüft ob neue Explosionen Partikel brauchen. */
  private _checkSpawn(lc: ReadonlyMap<string, SunLifecycleEntry>): void {
    for (const [id, entry] of lc) {
      if (entry.state !== SunState.Exploding) continue;
      if (this._spawned.has(id)) continue;
      this._spawned.add(id);
      const pos = this._sunPositions.get(id);
      if (pos) this._spawnBurst(pos[0], pos[1]);
    }
  }

  /** Spawnt einen Burst von Partikeln an der gegebenen Weltposition. */
  private _spawnBurst(wx: number, wz: number): void {
    let spawned = 0;
    for (let i = 0; i < MAX_PARTICLES && spawned < MAX_PARTICLES; i++) {
      const p = this._particles[i];
      if (p.active) continue;
      const angle = (spawned / MAX_PARTICLES) * Math.PI * 2 + Math.random() * 0.3;
      const elev = (Math.random() - 0.5) * 1.4;
      const cosE = Math.cos(elev);
      p.active = true;
      p.originX = wx;
      p.originZ = wz;
      p.dirX = Math.cos(angle) * cosE;
      p.dirY = Math.sin(elev);
      p.dirZ = Math.sin(angle) * cosE;
      p.speed = PARTICLE_SPEED * (0.6 + Math.random() * 0.8);
      p.elapsed = 0;
      p.seed = Math.random();
      spawned++;
    }
  }

  private _updateParticles(dt: number): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this._particles[i];
      if (!p.active) continue;
      p.elapsed += dt;
      if (p.elapsed >= PARTICLE_LIFETIME) {
        p.active = false;
      }
    }
  }

  private _uploadInstances(): void {
    let count = 0;
    const buf = this._instanceBuf;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this._particles[i];
      if (!p.active) continue;
      const t = p.elapsed;
      const decel = 1.0 - (t / PARTICLE_LIFETIME) * 0.6; // Abbremsen
      const dist = p.speed * t * decel;
      const idx = count * FLOATS_PER_PARTICLE;
      buf[idx] = p.originX + p.dirX * dist;
      buf[idx + 1] = p.dirY * dist;
      buf[idx + 2] = p.originZ + p.dirZ * dist;
      buf[idx + 3] = PARTICLE_SIZE * (1.0 - t / PARTICLE_LIFETIME * 0.7);
      buf[idx + 4] = 1.0 - (t / PARTICLE_LIFETIME); // Alpha
      buf[idx + 5] = p.seed;
      count++;
    }

    this._activeCount = count;
    if (count > 0) {
      this._mesh.updateInstances(buf.subarray(0, count * FLOATS_PER_PARTICLE), count);
    }
  }
}
